import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/authStore';
import { suggestBudgets, useBudgetStore } from '@flows.cash/core';
import type { AiBudgetSuggestionsResult, SuggestBudgetsErrorCode } from '@flows.cash/types';

interface AiBudgetSuggestionsProps {
  open: boolean;
  onClose: () => void;
}

// "Suggest from my spending" — asks the suggest-budgets Edge Function for
// per-category monthly budget proposals from real history, then lets the user
// pick/edit which to apply. Applying goes through the normal budget store
// (source: 'ai_suggested'); the rule-based Smart Assistant stays untouched as
// the path for accounts without history.
export function AiBudgetSuggestions({ open, onClose }: AiBudgetSuggestionsProps) {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuthStore();
  const { addBudget, updateBudget, fetchBudgets } = useBudgetStore();

  const [result, setResult] = useState<AiBudgetSuggestionsResult | null>(null);
  const [errorCode, setErrorCode] = useState<SuggestBudgetsErrorCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setResult(null);
      setErrorCode(null);
      const { data, error } = await suggestBudgets({
        months: 6,
        locale: (i18n.language || 'en').slice(0, 2),
      });
      if (cancelled) return;
      setLoading(false);
      if (error) {
        setErrorCode(error.error);
        return;
      }
      setResult(data);
      setSelected(new Set(data.suggestions.map((s) => s.categoryId)));
      setAmounts(
        Object.fromEntries(data.suggestions.map((s) => [s.categoryId, String(s.suggestedAmount)]))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [open, i18n.language]);

  const toggle = (categoryId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const handleApply = async () => {
    if (!user || !result) return;
    const chosen = result.suggestions.filter((s) => selected.has(s.categoryId));
    if (chosen.length === 0) return;

    setApplying(true);
    let created = 0;
    let adjusted = 0;
    let failed = 0;

    for (const s of chosen) {
      const amount = parseFloat(amounts[s.categoryId] ?? '') || s.suggestedAmount;
      try {
        if (s.action === 'adjust' && s.existingBudgetId) {
          await updateBudget(user.id, s.existingBudgetId, {
            amount,
            updated_at: new Date().toISOString(),
          } as any);
          adjusted++;
        } else {
          const now = new Date();
          const timestamp = now.toISOString();
          await addBudget(user.id, {
            user_id: user.id,
            name: s.categoryName,
            amount,
            currency: s.currency,
            category_id: s.categoryId,
            period: 'monthly',
            start_date: timestamp,
            end_date: new Date(now.getFullYear() + 10, now.getMonth(), 1).toISOString(),
            spent: 0,
            remaining: amount,
            is_archived: false,
            source: 'ai_suggested',
            created_at: timestamp,
            updated_at: timestamp,
          } as any);
          created++;
        }
      } catch (err) {
        console.error('Failed to apply budget suggestion:', s.categoryName, err);
        failed++;
      }
    }

    setApplying(false);
    if (created || adjusted) {
      toast.success(
        t('budgets.ai.applied', {
          defaultValue: 'Applied {{count}} budget suggestions',
          count: created + adjusted,
        })
      );
      fetchBudgets(user.id);
      onClose();
    }
    if (failed) {
      toast.error(
        t('budgets.ai.applyFailed', {
          defaultValue: '{{count}} suggestions failed to apply',
          count: failed,
        })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !applying && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-secondary dark:text-primary-dark" />
            {t('budgets.ai.title', 'Budget suggestions from your spending')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'budgets.ai.subtitle',
              'AI analyzed your last 6 months of spending. Review, edit, and apply the budgets you want.'
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('budgets.ai.loading', 'Analyzing your spending history…')}
          </div>
        )}

        {errorCode === 'insufficient_data' && (
          <p className="py-6 text-sm text-text-secondary dark:text-text-dark-secondary">
            {t(
              'budgets.ai.insufficientData',
              'Not enough transaction history yet — add a couple of months of transactions, or try the Smart Assistant questionnaire instead.'
            )}
          </p>
        )}
        {errorCode === 'rate_limited' && (
          <p className="py-6 text-sm text-text-secondary dark:text-text-dark-secondary">
            {t('budgets.ai.rateLimited', 'Too many requests — try again shortly.')}
          </p>
        )}
        {errorCode === 'suggest_unavailable' && (
          <p className="py-6 text-sm text-text-secondary dark:text-text-dark-secondary">
            {t('budgets.ai.unavailable', "Suggestions aren't available right now — try again later.")}
          </p>
        )}

        {result && (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">{result.summary}</p>
            {result.warnings.map((w, i) => (
              <p key={i} className="text-xs text-amber-600 dark:text-amber-400">
                {w}
              </p>
            ))}

            <div className="space-y-2">
              {result.suggestions.map((s) => {
                const isSelected = selected.has(s.categoryId);
                const delta =
                  s.action === 'adjust' && s.existingAmount != null
                    ? s.suggestedAmount - s.existingAmount
                    : null;
                return (
                  <div
                    key={s.categoryId}
                    className={`p-3 rounded-xl border transition-colors ${
                      isSelected
                        ? 'border-secondary dark:border-primary-dark bg-secondary/5 dark:bg-primary-dark/5'
                        : 'border-border dark:border-border-dark opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(s.categoryId)}
                        className="h-4 w-4 rounded accent-current"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary truncate">
                            {s.categoryName}
                          </span>
                          {s.action === 'adjust' && delta !== null && (
                            <span
                              className={`inline-flex items-center gap-0.5 text-xs ${
                                delta < 0 ? 'text-emerald-600' : 'text-amber-600'
                              }`}
                            >
                              {delta < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                              {t('budgets.ai.adjustFrom', {
                                defaultValue: 'now {{amount}}',
                                amount: s.existingAmount,
                              })}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5">
                          {s.reasoning}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Input
                          type="number"
                          min="0"
                          value={amounts[s.categoryId] ?? ''}
                          onChange={(e) =>
                            setAmounts((prev) => ({ ...prev, [s.categoryId]: e.target.value }))
                          }
                          disabled={!isSelected}
                          className="w-28 text-right"
                        />
                        <span className="text-xs text-text-secondary dark:text-text-dark-secondary w-10">
                          {s.currency}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                {t('budgets.ai.disclaimer', 'Based on your data — informational, not financial advice.')}
              </p>
              <Button onClick={handleApply} disabled={applying || selected.size === 0}>
                {applying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('budgets.ai.apply', {
                  defaultValue: 'Apply {{count}} budgets',
                  count: selected.size,
                })}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
